import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Modal } from "@vimeo/iris/components";
import { ArrowLeft } from "@vimeo/iris/icons";
import { Text } from "@vimeo/iris/typography";

import EmbedInEmailContext from "../../contexts/embedInEmailContext";

import { Body } from "./Body";
import { Footer, Step } from "./Footer";
import { ModalHeader } from "../../components/ModalHeader";
import { ModalStyled } from "../../components/modal.styles";
import {
    EmbedInEmailSource,
    EventName,
    sendAnalyticsEvent,
} from "../../analytics/common";
import { getVideoIdFromUri } from "../../common";

interface Props {
    useModal: boolean;
    /**
     * The source from which the Embed in Email experience is being launched.
     */
    source: EmbedInEmailSource;
    /**
     * Controls the visibility of the modal.
     * Used only when the component is
     * rendered as a modal.
     */
    show?: boolean;
    /**
     * Required if `modal` property is set to `true`.
     */
    onClose?: () => void;
    /**
     * Whether the asset selection step should be hidden.
     * Used when embedding a thumbnail or gif in email
     * directly from the Thumbnails or Gifs settings pages.
     */
    hideAssetSelection?: boolean;
}

export function EmbedInEmail(props: Props) {
    if (props.useModal && !props.onClose) {
        throw new Error("onClose is required if useModal is true");
    }

    const [embedInEmailContextState, setEmbedInEmailContextState] =
        useContext(EmbedInEmailContext);
    if (!embedInEmailContextState.video) {
        throw new Error("Video is required");
    }

    useEffect(() => {
        if (!embedInEmailContextState.video) {
            return;
        }
        const videoId = getVideoIdFromUri(embedInEmailContextState.video.uri);

        setEmbedInEmailContextState({
            ...embedInEmailContextState,
            name: embedInEmailContextState.video.name,
            videoId,
            source: props.source,
        });

        sendAnalyticsEvent(EventName.pageView, {
            name: "embed_in_email",
            video_id: videoId,
            location: props.source,
            section: null,
        }, 2);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The below IDs are used to send analytics events when the respective
    // steps are completed. See the `onStepClick` function below.
    let steps: Step[] = [
        { id: 0, label: "Choose your asset", value: "choose-asset" },
        { id: 1, label: "Configure your asset", value: "configure-asset" },
        { id: 2, label: "Create asset", value: "create-asset" },
        { id: 3, label: "Success", value: "success" },
    ];
    if (props.hideAssetSelection) {
        steps.splice(0, 1);
        // Decrement the step id's since we just removed a step.
        steps = steps.map((s) => ({
            id: s.id - 1,
            label: s.label,
            value: s.value,
        }));
    }
    const [selectedStep, setSelectedStep] = useState(steps[0]);
    const [completedSteps, setCompletedSteps] = useState<Pick<Step, "id">[]>(
        []
    );

    function onStepClick(event: any, data: any) {
        if (data.step.id === 0) {
            // Send analytics event since the user chose an asset,
            // and clicked Next.
            sendAnalyticsEvent(EventName.integrationsClick, {
                name: "select_asset_embed_in_email",
                location: embedInEmailContextState.source,
                video_id: embedInEmailContextState.videoId,
                asset_type: embedInEmailContextState.selectedImgType,
                cta: "next",
            });
        } else if (data.step.id === 1) {
            // Send analytics event for completion of configure
            // asset step.
            sendAnalyticsEvent(EventName.integrationsClick, {
                name: "click_to_configure_asset_embed_in_email",
                asset_type: embedInEmailContextState.selectedImgType,
                location: embedInEmailContextState.source,
                video_id: embedInEmailContextState.videoId,
                cta: "next",
            });
        }
        setCompletedSteps(steps.slice(0, data.step.id));
        setSelectedStep(data.step);
    }

    function resetSteps() {
        setSelectedStep(steps[0]);
        setCompletedSteps([]);
    }

    const body = (
        <Body
            onComplete={() => {
                setCompletedSteps(steps.slice(0, steps.length - 1));
                setSelectedStep(steps[steps.length - 1]);
            }}
            selectedStep={selectedStep}
            video={embedInEmailContextState.video}
        />
    );

    const footer = (
        <Footer
            selectedStep={selectedStep}
            completedSteps={completedSteps}
            steps={steps}
            onClose={() => {
                if (props.onClose) {
                    props.onClose();
                }

                resetSteps();

                // If this component is rendered inside a modal, then
                // there is nothing to navigate back to.
                if (props.useModal) {
                    return;
                }

                navigate(-1);
            }}
            onStepClick={onStepClick}
            disableNextButton={!embedInEmailContextState.imgUrl}
        />
    );

    function onModalClose() {
        if (props.onClose) {
            props.onClose();
        }

        resetSteps();
    }

    const modal = (
        <Modal
            active={!!props.show}
            size="lg"
            onClose={onModalClose}
            content={
                <ModalStyled>
                    <ModalHeader
                        onCloseClicked={onModalClose}
                        title="Embed in email"
                    ></ModalHeader>
                    <div style={{ width: "600px", minHeight: "350px" }}>
                        <Text>{selectedStep.label}</Text>
                        {body}
                    </div>
                    <Modal.Footer>{footer}</Modal.Footer>
                </ModalStyled>
            }
        />
    );

    const navigate = useNavigate();

    const regular = (
        <div className="slds-var-p-around_medium">
            <div className="slds-grid slds-grid_align-spread">
                <div className="slds-col">
                    <div className="slds-grid">
                        <div className="slds-col slds-var-m-right_small">
                            <Button
                                format="secondary"
                                variant="minimal"
                                icon={<ArrowLeft color="#000000" />}
                                onClick={() => navigate(-1)}
                                size="xs"
                            />
                        </div>
                        <div className="slds-col">
                            <div className="slds-text-heading_small">
                                Embed in Email
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {body}
            <div
                className="slds-is-fixed slds-var-m-around_medium"
                style={{ display: "flex", left: 0, right: 0, bottom: 0 }}
            >
                {footer}
            </div>
        </div>
    );

    return <>{props.useModal ? modal : regular}</>;
}
